import classNames from "classnames";
import { AddOwnersModal } from "PFApp/activities/show/parts/add_owners_modal";
import AppContext from "PFApp/app_context";
import SidePanel from "PFApp/booking/parts/overview/side_panel";
import { useMetadata } from "PFApp/components/activity_commons/activity_details_more_info";
import { TEMPLATE_KEYS } from "PFApp/constants/templates";
import { Button } from "PFComponents/button";
import CustomValuesList from "PFComponents/custom_values_list/custom_values_list";
import Tooltip from "PFComponents/tooltip/tooltip";
import { useAvailabilityFormatter } from "PFCore/helpers/availability";
import { getProfileName } from "PFCore/helpers/profile";
import { useGetFormattedPostUntil } from "PFCore/helpers/use_get_formatted_post_until";
import { useReadableAvailability } from "PFCore/helpers/use_readable_availability";
import { useBookingActivity } from "PFCore/hooks/queries/activity/use_booking_activity";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import CalendarIcon from "PFIcons/calendar_fixed.svg";
import CheckIcon from "PFIcons/check.svg";
import ClipboardListIcon from "PFIcons/clipboard_list.svg";
import HourglassIcon from "PFIcons/hourglass.svg";
import InfoIcon from "PFIcons/info.svg";
import PropTypes from "prop-types";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";

import { useRowIconComponent } from "../../../hooks/use_row_icon_component";
import { BookingDetailsActivity } from "../../../parts/overview/detail_view/booking_detail/booking_details_activity";
import { useActivityLink } from "../../../parts/overview/detail_view/booking_detail/use_activity_link";
import css from "../../../parts/overview/detail_view/detail_view.module.scss";
import { ShortlistDetail } from "../../../parts/overview/detail_view/shortlist/shortlist_detail";
import BookingActivityContextProvider, {
  useBookingActivityContext
} from "../../../parts/providers/booking_activity_context_provider";
import { useDetailsPanelContext } from "../details_panel_context_provider";
import { DetailsHeader } from "./details_header";

const SKILLS_LIMIT = 5;

const CustomFields = ({ activity }) => {
  const { t } = useTranslation("bookingModule");

  const metadata = useMetadata({ activity });

  const filteredMetadata = metadata.filter(
    ({ type, title }) => "availability" !== type && title !== "Skills"
  );

  return (
    <span className={classNames(css.details, css.item)}>
      <div className={css.item}>
        <div className={css.label}>{t("privacy")}</div>
        <span className={css.alignCenter}>
          {t(activity.private ? "private" : "public")}
          {activity.private && (
            <Tooltip content={t("details.privacyTooltipText")} appendTo={"parent"}>
              <span className={css.alignCenter}>
                <InfoIcon height={20} width={20} className={css.icon} />
              </span>
            </Tooltip>
          )}
        </span>
      </div>
      {filteredMetadata.map(({ title, text }) => (
        <div key={title} className={css.item}>
          <div className={css.label}>{title}</div>
          <span>{text}</span>
        </div>
      ))}
    </span>
  );
};

CustomFields.propTypes = {
  activity: PropTypes.object.isRequired
};

const Details = withTranslation("bookingModule")(({ created_at, post_until, vacancies, t }) => {
  const { formatDate, utc } = useDateFormatter();

  const { timeHeader, timeValue } = useGetFormattedPostUntil(post_until);
  const vacanciesTotal = vacancies?.total || 0;
  const vacanciesBooked = (vacancies?.booked || 0) + (vacancies?.confirmed || 0);

  const detailsData = useMemo(
    () => [
      {
        title: timeHeader,
        icon: <HourglassIcon viewBox="7 1 12 17" className={css.icon} />,
        text: timeValue
      },
      {
        title: t("createdAt"),
        icon: <CalendarIcon viewBox="3 0 20 20" className={css.icon} />,
        text: formatDate(utc(created_at))
      },
      {
        title: t("vacancies"),
        icon: <ClipboardListIcon viewBox="2 -1 21 23" className={css.icon} />,
        text: vacanciesTotal
      },
      {
        title: t("booked"),
        icon: <CheckIcon viewBox="80 -170 512 800" className={css.icon} />,
        text: vacanciesBooked
      }
    ],
    [post_until, created_at, vacancies]
  );

  return (
    <span className={classNames(css.details, css.item)}>
      {detailsData.map(({ title, icon, text }) => (
        <span key={title} className={css.item}>
          <div className={css.label}>{title}</div>
          <span className={css.alignCenter}>
            {icon}
            {text}
          </span>
        </span>
      ))}
    </span>
  );
});

Details.displayName = "Details";
Details.propTypes = {
  post_until: PropTypes.string.isRequired,
  created_at: PropTypes.string.isRequired,
  vacancies: PropTypes.arrayOf(PropTypes.object)
};

const Duration = withTranslation("bookingModule")(({ availability, t }) => {
  const { readableAvailability } = useReadableAvailability({ availability });
  const { availabilityRangesFormat } = useAvailabilityFormatter();
  return (
    <span className={classNames(css.item, css.duration, css.spaceBetween)}>
      <span>
        <div className={css.alignCenter}>
          <CalendarIcon viewBox="3 0 20 20" height={20} fill="var(--Palette-base)" />
          <span className={css.label}>{t("duration")} </span>
          {availability?.ranges && (
            <Tooltip
              content={availabilityRangesFormat(availability, true, css.tooltipContent)}
              appendTo={"parent"}
            >
              <span className={css.alignCenter}>
                <InfoIcon viewBox="0 0 20 16" height={20} width={20} className={css.icon} />
              </span>
            </Tooltip>
          )}
        </div>
        {readableAvailability}
      </span>
    </span>
  );
});

Duration.displayName = "Duration";
Duration.propTypes = {
  availability: PropTypes.object.isRequired
};

const Owners = withTranslation("bookingModule")(({ t, activity }) => {
  const { owners, coowned } = activity;
  const [showAddOwners, setShowAddOwners] = useState(false);

  return (
    <div className={css.item}>
      <div className={css.label}> {t("owner")}</div>
      <span className={classNames(css.strong, css.spaceBetween)}>
        {(owners || []).map(({ profile }) => getProfileName(profile)).join(", ")}
        {coowned && <Button icon="add" kind="tertiary" onClick={() => setShowAddOwners(true)} />}
      </span>
      {showAddOwners && <AddOwnersModal handleClose={() => setShowAddOwners(false)} activity={activity} />}
    </div>
  );
});

Owners.displayName = "Owners";
Owners.propTypes = {
  activity: PropTypes.object.isRequired
};

const Skills = withTranslation("bookingModule")(({ currentProfile, currentAccount, custom_fields, t }) => {
  const skills = (custom_fields || []).find(({ type }) => type.name === "skills");
  const skillsType = currentProfile.custom_types.find(({ name }) => name === "skills");

  return (
    skills?.values.length > 0 && (
      <span className={css.item}>
        <div className={css.label}>{t("skills")}</div>
        <CustomValuesList
          type={skillsType}
          customValues={skills.values}
          showTips={true}
          displayAttributeLevel={true}
          profileId={currentProfile.id}
          moreLimit={SKILLS_LIMIT}
          currentProfile={currentProfile}
          currentAccount={currentAccount}
          showModal
        />
      </span>
    )
  );
});

Skills.displayName = "Skills";
Skills.propTypes = {
  currentProfile: PropTypes.object.isRequired,
  currentAccount: PropTypes.object.isRequired,
  custom_fields: PropTypes.arrayOf(PropTypes.object)
};

const Buttons = withTranslation("bookingModule")(({ setAllocateManually, t }) => {
  const { activity, parentActivity } = useBookingActivityContext();
  const vacanciesTotal = activity?.vacancies.total;
  const shortlistsTotal = activity?.shortlists.total;
  const isCompleted = activity?.state === "complete" || parentActivity?.state === "complete";

  const renderButton = () => (
    <div className={css.buttons}>
      <Button
        text={t("allocateManually")}
        className={css.allocateManuallyButton}
        kind="tertiary"
        disabled={!vacanciesTotal || shortlistsTotal === 0 || isCompleted}
        onClick={() => setAllocateManually((value) => !value)}
      />
    </div>
  );

  return !isCompleted ? (
    renderButton()
  ) : (
    <Tooltip content={t("bookingNotAvailable")}>{renderButton()}</Tooltip>
  );
});

Buttons.displayName = "Buttons";
Buttons.propTypes = {
  setAllocateManually: PropTypes.func.isRequired
};

const RoleDetailContent = ({ onClose }) => {
  const {
    store: { currentProfile, currentAccount }
  } = useContext(AppContext);
  const { detailsData } = useDetailsPanelContext();

  const { t } = useTranslation("bookingModule");
  const { activity, canEdit } = useBookingActivityContext();
  const {
    data: parentActivity,
    isLoading: isParentActivityLoading,
    isError: isParentActivityError
  } = useBookingActivity(activity?.parent_activity_id);
  const { bookingCategories } = useBookingCategories();
  const hasWriteAccessToAtLeastOneCategory = bookingCategories.some((i) => !i.readonly);
  const ActivityIconComponent = useRowIconComponent({
    templateId: activity?.template_id,
    type: TEMPLATE_KEYS.ROLE
  });

  const [allocateManually, setAllocateManually] = useState(false);
  const isAuditRole = useMemo(
    () =>
      activity
        ? currentProfile.templates.find(({ id }) => id === activity?.template_id)?.key ===
          TEMPLATE_KEYS.AUDIT_ROLE
        : undefined,
    [activity]
  );

  useEffect(() => {
    if (!detailsData.openAllocateManually) {
      return;
    }
    if (isAuditRole !== undefined && hasWriteAccessToAtLeastOneCategory) {
      setAllocateManually(!isAuditRole);
    }
  }, [detailsData.openAllocateManually, isAuditRole, hasWriteAccessToAtLeastOneCategory]);

  const linkUrl = useActivityLink({ activity, parentActivity });

  if (!activity) {
    return null;
  }

  const { post_until, custom_fields, name, description, created_at, profile } = activity;

  return (
    <>
      <SidePanel show={allocateManually} onClose={() => setAllocateManually(false)} secondPanel>
        <ShortlistDetail showList={allocateManually} />
      </SidePanel>
      <div className={classNames(css.root, css.roleDetail)}>
        <DetailsHeader
          title={name}
          subtitle={isAuditRole ? t("auditRole") : t("role")}
          onClose={onClose}
          linkUrl={linkUrl}
        >
          <ActivityIconComponent
            iconProps={{ viewBox: "4 0 20 20", height: 35, fill: "var(--Palette-text)" }}
            wrapperProps={{
              classes: {
                container: css.auditActivityIconContainer,
                badge: css.auditActivityIconBadge
              },
              badgeIconProps: { width: 12 }
            }}
          />
        </DetailsHeader>
        <Owners activity={activity} />
        <div className={css.item}>
          <div className={css.label}> {t("description")}</div>
          <span>{description}</span>
        </div>
        {!isParentActivityError && (
          <div className={css.item}>
            <div className={css.separator} />
            <BookingDetailsActivity activity={parentActivity} isLoading={isParentActivityLoading} />
            <div className={css.separator} />
          </div>
        )}
        <Duration availability={activity.availability} />
        <Details created_at={created_at} post_until={post_until} vacancies={activity.vacancies} />
        <div className={css.separator} />
        {canEdit && !isAuditRole && hasWriteAccessToAtLeastOneCategory && (
          <>
            <Buttons setAllocateManually={setAllocateManually} />
            <div className={css.separator} />
          </>
        )}
        <Skills
          custom_fields={custom_fields}
          currentProfile={currentProfile}
          currentAccount={currentAccount}
        />
        <CustomFields activity={activity} />
        <div className={css.separator} />
        <div className={css.item}>
          <div className={css.label}>{t("creator")}</div>
          <span>{getProfileName(profile)}</span>
        </div>
      </div>
    </>
  );
};

const RoleDetail = ({ data, onClose }) => (
  <BookingActivityContextProvider id={data.id}>
    <RoleDetailContent onClose={onClose} />
  </BookingActivityContextProvider>
);

RoleDetail.propTypes = {
  data: PropTypes.shape({
    type: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired
  }),
  onClose: PropTypes.func.isRequired
};

RoleDetailContent.propTypes = {
  onClose: PropTypes.func.isRequired
};

export default RoleDetail;
